import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRef } from "react";
import { FieldValues, UseFormReturn, FieldPath } from "react-hook-form";
import FormFieldTooltip from "./FormFieldTooltip";

type Option = {
  label: string;
  value: string;
};

type FormFieldSelectPropsType<T extends FieldValues> = {
  form: UseFormReturn<T, unknown, undefined>;
  placeholder: string;
  label: string;
  name: FieldPath<T>;
  options: readonly Option[];
  description?: string;
  tooltip?: string;
  onChange?: ((value: string) => void) | ((value: string) => Promise<void>);
  className?: string;
};

function FormFieldSelect<T extends FieldValues>({
  form,
  placeholder,
  label,
  name,
  options,
  description,
  tooltip,
  onChange,
}: FormFieldSelectPropsType<T>) {
  const itemRef = useRef<HTMLDivElement>(null);
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem ref={itemRef}>
          <FormLabel>
            {label}
            {tooltip && <FormFieldTooltip content={tooltip} />}
          </FormLabel>
          <Select
            // Select jest niekontrolowany (defaultValue) — gdy formularz
            // wyczyści pole (np. "Wyczyść sekcję 7.3"), remount przez key
            // przywraca placeholder zamiast starej wartości.
            key={field.value ? "has-value" : "empty"}
            onValueChange={(value) => {
              if (onChange !== undefined) onChange(value);
              field.onChange(value);
            }}
            defaultValue={field.value}
          >
            <FormControl>
              <SelectTrigger className={fieldState.invalid ? "border-destructive focus:ring-destructive" : undefined}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent style={{ maxWidth: itemRef.current?.clientWidth }}>
              {options.map(({ value, label }) => (
                <SelectItem className="max-w-full" value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default FormFieldSelect;
